import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Input, Label, ButtonGroup, Button } from "reactstrap";

const MultiSelectFilter = ({ filters = [], onChange }) => {
  const selectAllRefs = useRef({});
  const [selectAllStates, setSelectAllStates] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({});
  const [activeFilters, setActiveFilters] = useState(null);

  useEffect(() => {
    const initialState = {};
    filters.forEach((filter) => {
      // Create Ref
      selectAllRefs.current[filter.accessor] = React.createRef();

      // Set default selected items (all items selected by default)
      initialState[filter.accessor] = filter.data;
    });

    /*
    setSelectedFilters(initialState)
    setSelectAllStates(
      filters.reduce((acc, filter) => {
        acc[filter.accessor] = true
        return acc
      }, {})
    )
    */
  }, []);

  const handleSelectAll = (filterType) => {
    let selected = !selectAllStates[filterType];

    setSelectAllStates((prev) => ({
      ...prev,
      [filterType]: selected,
    }));

    setSelectedFilters((prev) => {
      const newFilters = {
        ...prev,
        [filterType]: selected ? filters.find((f) => f.accessor === filterType).data : [],
      };

      if (onChange) {
        onChange(
          filters.map((f) => ({
            label: f.label,
            accessor: f.accessor,
            data: newFilters[f.accessor] || [],
          }))
        );
      }

      return newFilters;
    });
  };

  useEffect(() => {
    filters.forEach((filter) => {
      const checkbox = selectAllRefs.current[filter.accessor]?.current;
      if (checkbox) {
        const selectedCount = selectedFilters[filter.accessor]?.length || 0;
        if (selectedCount === 0) {
          checkbox.checked = false;
          checkbox.indeterminate = false;
        } else if (selectedCount === filter.data.length) {
          checkbox.checked = true;
          checkbox.indeterminate = false;
        } else {
          checkbox.checked = false;
          checkbox.indeterminate = true;
        }
      }
    });
  }, [selectedFilters, filters]);

  const handleItemSelect = (filterType, itemId) => {
    const filter = filters.find((f) => f.accessor === filterType);
    const item = filter.data.find((item) => item.id === itemId);

    setSelectedFilters((prev) => {
      const currentSelected = prev[filterType] || [];
      const isSelected = currentSelected.some((i) => i.id === itemId);

      const newSelected = isSelected ? currentSelected.filter((i) => i.id !== itemId) : [...currentSelected, item];

      const newFilters = {
        ...prev,
        [filterType]: newSelected,
      };

      if (onChange) {
        onChange(
          filters.map((f) => ({
            label: f.label,
            accessor: f.accessor,
            data: newFilters[f.accessor] || [],
          }))
        );
      }

      return newFilters;
    });
  };

  const renderFilter = (accessor) => {
    const filter = filters.find((f) => f.accessor === accessor);
    return (
      <div key={filter.accessor} className="filter-group mb-2">
        <div className="d-flex gap-3 align-items-center flex-wrap w-100 text-xl">
          <div className="form-check gap-2 d-flex items-center justify-center">
            <input type="checkbox" id={`${filter.accessor}-all`} className="form-check-input" key={`filter-group-${filter.accessor}`} ref={selectAllRefs.current[filter.accessor]} checked={selectAllStates[filter.accessor] || false} onChange={() => {}} onClick={() => handleSelectAll(filter.accessor)} />
            <Label className="form-check-label" for={`${filter.accessor}-all`}>
              ทั้งหมด
            </Label>
          </div>
          {filter.data.map((item) => (
            <div key={item.id} className="form-check gap-2 d-flex items-center justify-center">
              <Input type="checkbox" id={`${filter.accessor}-${item.id}`} className="form-check-input" checked={selectedFilters[filter.accessor]?.some((i) => i.id === item.id) || false} onChange={() => {}} onClick={() => handleItemSelect(filter.accessor, item.id)} />
              <Label className="form-check-label" for={`${filter.accessor}-${item.id}`}>
                {item.name}
              </Label>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="multi-select">
      <div className="d-flex gap-4 mb-2 align-items-center">
        <div className="text-xl fw-semibold text-gray-700">ค้นหาจาก...</div>
        <ButtonGroup className="dropdown gap-3">
          {filters.map((filter) => (
            <Button
              key={`${filter.accessor}-header`}
              onClick={() => {
                if (filter.accessor === activeFilters) {
                  setActiveFilters(null);
                } else {
                  setActiveFilters(filter.accessor);
                }
              }}
              active={activeFilters === filter.accessor}
              className={activeFilters === filter.accessor ? "text-gray-700 fw-semibold" : "text-gray-500 fw-semibold"}
            >
              <h4>{filter.label}</h4>
              <i className={`mdi mdi-chevron-down chevron-icon ${activeFilters === filter.accessor ? "rotate" : ""}`} />
            </Button>
          ))}
        </ButtonGroup>
      </div>
      {filters.map((filter) => (
        <div
          key={filter.accessor}
          style={{
            display: activeFilters === filter.accessor ? "block" : "none",
          }}
        >
          {renderFilter(filter.accessor)}
        </div>
      ))}
    </div>
  );
};

MultiSelectFilter.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func,
};

export default MultiSelectFilter;
